// web/src/components/CallDetailDrawer.tsx —— 通话详情抽屉（入口）：取数与状态编排 + 组合。
// 展示分片见 web/src/components/CallDetailDrawer/ 目录（转写视图 / 回拨与挂断标签 / AI 日志与流水线 / 详情页签内容 / 内部日志推导）。
// 既有调用方 import 路径与导出名不变（转写视图、回拨链接、挂断标签、AI 日志时间线在此逐名重导出）。

import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { Button, Empty, Message, Modal, Popconfirm, Space, Typography } from '@arco-design/web-react';
import { IconFile } from '@arco-design/web-react/icon';
import BccTabs, { BccTabPane } from './BccTabs';
import DetailDrawer, { DRAWER_WIDTH } from './DetailDrawer';
import FlowTraceView from './FlowTraceView';
import FormModal from './FormModal';
import RunQualityModal from './RunQualityModal';
import StatusTag from './StatusTag';
import SummaryModal from './SummaryModal';
import TicketCreateModal from './TicketCreateModal';
import TicketDetailDrawer from './TicketDetailDrawer';
import CallDetailPane from './CallDetailDrawer/CallDetailPane';
import { AILogView } from './CallDetailDrawer/AiLogView';
import { intentDisplay } from './CallDetailDrawer/_aiCallLog';
import { getAILogs, getCdr, getFlowTrace, type AICallLog, type Cdr, type FlowTrace } from '../api/cdr';
import { listCallSummaries, type CallSummary } from '../api/callSummaries';
import { getReport, listReports, type QualityReport } from '../api/quality';
import { usePerm } from '../hooks/usePerm';
import { usePhoneStore } from '../store/phone';
import { fmtDuration, fmtTime } from '../utils/format';
import { talkSeconds, waitSeconds } from '../utils/callTime';
import { useIntentNames } from '../utils/intentName';

// 公共导出面：以下名称由本文件重导出，既有调用方 import 路径与导出名不变
export { AILogView };
export { transcriptSegKey, TranscriptView } from './CallDetailDrawer/CallTranscriptView';
export { CallBackLink, HangupCauseTag } from './CallDetailDrawer/CallDetailBits';

// 完整报告弹层按需加载：报告详情本体在 pages/quality，动态导入避免 components ↔ pages 静态循环
const ReportDetailModal = lazy(() => import('../pages/quality/ReportDetailModal'));

export interface CallDetailDrawerProps {
  visible: boolean;
  /** 要打开详情的 CDR id（null/undefined 不加载） */
  cdrId?: number | null;
  onClose: () => void;
}

/**
 * 通话详情抽屉（抽离自 calls/index.tsx，供呼叫记录页与坐席工作台客户动态区共用）：
 * 基本信息 + 录音播放 + 转写文本 + 服务小结（可补录）+ AI 调用日志 + 发起工单。
 * 打开时按 cdrId 拉取 getCdr / getAILogs / listCallSummaries。
 */
export default function CallDetailDrawer({ visible, cdrId, onClose }: CallDetailDrawerProps) {
  useIntentNames();
  const [detail, setDetail] = useState<Cdr | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [summary, setSummary] = useState<CallSummary | null>(null);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [ticketOpen, setTicketOpen] = useState(false);
  // 已有工单时的「重新发起/前往查看」确认框
  const [ticketConfirm, setTicketConfirm] = useState(false);
  // 工单详情弹窗：本页查看工单详情（不跳转工单管理页，保持浏览位置）
  const [ticketDetailId, setTicketDetailId] = useState<number | null>(null);
  const [aiLogs, setAiLogs] = useState<AICallLog[]>([]);
  const [aiLogsLoading, setAiLogsLoading] = useState(false);
  const [flowTrace, setFlowTrace] = useState<FlowTrace | null>(null);
  const [flowLoading, setFlowLoading] = useState(false);
  // 录音播放器引用（关闭抽屉/切换 CDR 时重置）
  const audioRef = useRef<HTMLAudioElement>(null);
  // 单条质检：该通话最新报告 + 触发/轮询状态（仅管理端 quality.manage 可见入口）
  const canManageQuality = usePerm('quality.manage');
  const [qualityReport, setQualityReport] = useState<QualityReport | null>(null);
  const [qualityPolling, setQualityPolling] = useState(false);
  const [runQualityOpen, setRunQualityOpen] = useState(false);
  // 完整质检报告弹窗（footer「查看报告」入口）
  const [qualityModalId, setQualityModalId] = useState<number | null>(null);

  // cdrId 打开时并行加载：详情（含 transcript/record_url）+ 服务小结 + AI 调用日志
  useEffect(() => {
    if (!visible || !cdrId) {
      setDetail(null);
      return;
    }
    audioRef.current?.pause();
    setDetail(null);
    setSummary(null);
    setAiLogs([]);
    let cancelled = false;
    setLoadingDetail(true);
    getCdr(cdrId)
      .then((d) => {
        if (!cancelled && d) setDetail(d);
      })
      .catch(() => undefined) // silent：不存在/未接通由调用方自行展示空态
      .finally(() => {
        if (!cancelled) setLoadingDetail(false);
      });
    listCallSummaries({ cdr_id: cdrId, page: 1, pageSize: 1 })
      .then(({ list }) => {
        if (!cancelled) setSummary(list[0] || null);
      })
      .catch(() => {
        if (!cancelled) setSummary(null);
      });
    setAiLogsLoading(true);
    getAILogs(cdrId)
      .then(({ logs }) => {
        if (!cancelled) setAiLogs(Array.isArray(logs) ? logs : []);
      })
      .catch(() => {
        if (!cancelled) setAiLogs([]);
      })
      .finally(() => {
        if (!cancelled) setAiLogsLoading(false);
      });
    setFlowLoading(true);
    getFlowTrace(cdrId)
      .then((t) => {
        if (!cancelled) setFlowTrace(t || null);
      })
      .catch(() => {
        if (!cancelled) setFlowTrace(null);
      })
      .finally(() => {
        if (!cancelled) setFlowLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [visible, cdrId]);

  // 该通话最新质检报告：先按记录查列表定位报告 id，再取详情（列表项不含违规点/维度明细对象），
  // 详情失败回退列表项，保证面板仍能显示总分
  const loadQualityReport = useCallback(async (id: number) => {
    const { list } = await listReports({ cdrId: id, scene: 'call', page: 1, pageSize: 5 });
    const first = list[0];
    if (!first) return null;
    try {
      return await getReport(first.id);
    } catch {
      return first;
    }
  }, []);

  useEffect(() => {
    if (!visible || !cdrId) {
      setQualityReport(null);
      setQualityPolling(false);
      return;
    }
    let cancelled = false;
    setQualityReport(null);
    loadQualityReport(cdrId)
      .then((rep) => {
        if (!cancelled) setQualityReport(rep);
      })
      .catch(() => {
        if (!cancelled) setQualityReport(null);
      });
    return () => {
      cancelled = true;
    };
  }, [visible, cdrId, loadQualityReport]);

  // 质检轮询：投递后每 3s 拉报告，报告出现即完成（最多约 5 分钟兜底）
  useEffect(() => {
    if (!qualityPolling || !visible || !cdrId) return;
    let alive = true;
    let timer: ReturnType<typeof setTimeout>;
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const rep = await loadQualityReport(cdrId);
        if (!alive) return;
        if (rep) {
          setQualityReport(rep);
          setQualityPolling(false);
          Message.success('质检完成');
          return;
        }
      } catch {
        /* 轮询静默失败：继续下一轮 */
      }
      if (!alive) return;
      if (attempts >= 100) {
        setQualityPolling(false);
        return;
      }
      timer = setTimeout(tick, 3000);
    };
    timer = setTimeout(tick, 3000);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [qualityPolling, visible, cdrId, loadQualityReport]);

  // 发起工单——已有工单则提示「重新发起/前往查看」，无工单直接打开创建弹窗
  const openTicketCreate = () => {
    if (detail?.ticket_id) {
      setTicketConfirm(true);
      return;
    }
    setTicketOpen(true);
  };

  // 关闭抽屉：录音播放中弹确认（继续=后台播放 / 停止=暂停后关闭）
  const handleClose = () => {
    const audio = audioRef.current;
    if (audio && !audio.paused && audio.currentTime > 0) {
      Modal.confirm({
        title: '录音正在播放',
        content: '是否继续播放录音？',
        okText: '继续',
        cancelText: '停止',
        onOk: () => onClose(),
        onCancel: () => {
          audio.pause();
          onClose();
        },
      });
      return;
    }
    onClose();
  };

  // 时长口径：通话时长=接通→挂断（录音同轴），等待接通=拨号/进线→接通（振铃/IVR/排队）
  const talkSec = talkSeconds(detail);
  const waitSec = waitSeconds(detail);
  // AI 会话摘要条：取实时识别意图（session_intent/转人工/模板）日志展示意图归属
  const intentLog = aiLogs.find(
    (l) => l.stage === 'session_intent' || l.stage === 'intent_transfer' || l.stage === 'intent_template'
  );
  const intentName = intentLog ? intentDisplay(intentLog) : undefined;

  return (
    <DetailDrawer
      width={DRAWER_WIDTH.medium}
      title={
        <span className="bcc-call-head">
          <span>
            通话详情
            {detail?.id ? <span className="bcc-call-head__id">#{detail.id}</span> : null}
          </span>
          {/* 时间/时长右对齐并与编号分开：左看编号、右对时间，避免一串文字挤在一起扫读不出 */}
          {detail?.id ? (
            <span className="bcc-call-head__meta">
              {fmtTime(detail.start_time || detail.created_at)} · {talkSec > 0 ? fmtDuration(talkSec) : '未接通'}
            </span>
          ) : null}
        </span>
      }
      visible={visible}
      onCancel={handleClose}
      loading={loadingDetail}
      footer={
        detail?.id ? (
          <Space style={{ justifyContent: 'flex-end' }}>
            {qualityReport && (
              <Button onClick={() => setQualityModalId(qualityReport.id)}>查看报告</Button>
            )}
            {canManageQuality &&
              (qualityReport ? (
                <Popconfirm
                  title="该通话已有质检报告，重新质检会一并删除现有报告与复核样本，且不可恢复。确认继续？"
                  okText="继续"
                  cancelText="取消"
                  onOk={() => setRunQualityOpen(true)}
                >
                  <Button loading={qualityPolling}>质检</Button>
                </Popconfirm>
              ) : (
                <Button loading={qualityPolling} onClick={() => setRunQualityOpen(true)}>
                  质检
                </Button>
              ))}
            <Button type="primary" icon={<IconFile />} onClick={openTicketCreate}>
              发起工单
            </Button>
          </Space>
        ) : undefined
      }
    >
      {detail ? (
        <BccTabs contentPadding={false} defaultActiveTab="detail">
          <BccTabPane key="detail" title="通话详情">
            <CallDetailPane
              detail={detail}
              intentName={intentName}
              aiLogCount={aiLogs.length}
              audioRef={audioRef}
              summary={summary}
              talkSec={talkSec}
              waitSec={waitSec}
              onSupplementSummary={() => setSummaryOpen(true)}
              onOpenTicket={(ticketId) => setTicketDetailId(ticketId)}
            />
          </BccTabPane>
          <BccTabPane key="ai-logs" title="AI 调用日志">
            <AILogView logs={aiLogs} loading={aiLogsLoading} />
          </BccTabPane>
          <BccTabPane key="flow" title="流程轨迹">
            <FlowTraceView trace={flowTrace} loading={flowLoading} />
          </BccTabPane>
        </BccTabs>
      ) : loadingDetail ? null : (
        <Empty description="未找到通话记录" />
      )}

      {/* 单条质检：规则集选择 → 异步投递 worker → 轮询报告 */}
      <RunQualityModal
        visible={runQualityOpen}
        scene="call"
        cdrId={cdrId}
        hasReport={!!qualityReport}
        onCancel={() => setRunQualityOpen(false)}
        onSubmitted={() => {
          setRunQualityOpen(false);
          setQualityReport(null);
          setQualityPolling(true);
        }}
      />

      {/* 查看完整质检报告（footer「查看报告」入口；详情变更后刷新面板） */}
      {qualityModalId != null && (
        <Suspense fallback={null}>
          <ReportDetailModal
            reportId={qualityModalId}
            visible
            onClose={() => setQualityModalId(null)}
            onChanged={() => {
              if (cdrId) void loadQualityReport(cdrId).then(setQualityReport).catch(() => undefined);
            }}
          />
        </Suspense>
      )}

      {/* 补录服务小结（与工作台挂断后弹窗同一组件，传入已定位的 CDR） */}
      <SummaryModal
        visible={summaryOpen}
        cdr={detail as Cdr | null}
        onClose={(submitted) => {
          setSummaryOpen(false);
          if (submitted && detail?.id) {
            listCallSummaries({ cdr_id: detail.id, page: 1, pageSize: 1 })
              .then(({ list }) => setSummary(list[0] || null))
              .catch(() => undefined);
          }
        }}
      />

      {/* 已有工单确认框（C1：重新发起 / 前往查看） */}
      <FormModal
        visible={ticketConfirm}
        title="该通话已存在工单"
        onCancel={() => setTicketConfirm(false)}
        footer={
          <Space>
            <Button
              onClick={() => {
                setTicketConfirm(false);
                if (detail?.ticket_id) setTicketDetailId(detail.ticket_id as number);
              }}
            >
              前往查看
            </Button>
            <Button
              type="primary"
              onClick={() => {
                setTicketConfirm(false);
                setTicketOpen(true);
              }}
            >
              重新发起
            </Button>
          </Space>
        }
      >
        <Space>
          该通话已存在工单
          <Typography.Text style={{ color: 'rgb(var(--link-6))' }}>#{detail?.ticket_id}</Typography.Text>
          {detail?.ticket_status ? <StatusTag value={detail.ticket_status} /> : null}
          ，是否重新发起工单？
        </Space>
      </FormModal>

      {/* 为历史通话发起工单（关联当前 CDR，AI 预填建单） */}
      <TicketCreateModal
        visible={ticketOpen}
        cdr={detail as Cdr | null}
        onClose={(created) => {
          setTicketOpen(false);
          if (created?.id) Message.success(`工单 #${created.id} 已创建`);
        }}
      />

      {/* 工单详情抽屉：本页查看（不跳转，保持浏览位置；「前往工单管理」才离开） */}
      <TicketDetailDrawer
        visible={!!ticketDetailId}
        ticketId={ticketDetailId}
        onClose={() => setTicketDetailId(null)}
      />
    </DetailDrawer>
  );
}
